import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";

import { useGetManagementRolesQuery } from "@/apps/admin/api/adminOnly/managementRoles";
import { useGetObjectUserByEmailQuery } from "@/apps/admin/api/adminOnly/objectUsers";
import { OBJECT_TYPE } from "@/apps/admin/enums/objectType";
import {
  selectUser,
  setPermissions,
  setPermissionsError,
} from "@/apps/admin/features/authentication/authenticationSlice";
import { denormalizePermissions } from "@/apps/admin/utilities/data";
import { getHttpStatus } from "@/state/api/createBaseQuery";

export class PermissionsLoadError extends Error {
  override name = "PermissionsLoadError";

  constructor(
    message = "Failed to load user permissions",
    public readonly cause?: unknown
  ) {
    super(message);
    Object.setPrototypeOf(this, PermissionsLoadError.prototype);
  }
}

// The auth service answers 403 (no management.read) or 404 (no management user) for users without access
const NO_ACCESS_STATUSES = [403, 404];

// A SerializedError (the request threw) carries no HTTP status
const httpStatusOf = (
  error: FetchBaseQueryError | SerializedError
): number | null => ("status" in error ? getHttpStatus(error) : null);

const PermissionService = () => {
  const dispatch = useDispatch();
  const email = useSelector(selectUser)?.email;

  const userQuery = useGetObjectUserByEmailQuery(
    // Skipped without an email, so the empty string is never sent
    { objectType: OBJECT_TYPE.MANAGEMENT, objectId: "*", email: email ?? "" },
    { skip: !email }
  );
  const rolesQuery = useGetManagementRolesQuery({}, { skip: !email });

  const queryError = userQuery.error ?? rolesQuery.error;
  const user = userQuery.data?.data;
  const roles = rolesQuery.data?.data;
  // RTK keeps the last successful data when a background refetch fails
  const hasData = user !== undefined && roles !== undefined;

  useEffect(() => {
    if (!email) {
      dispatch(setPermissions({ permissions: [] }));
      return;
    }
    const httpStatus = queryError ? httpStatusOf(queryError) : null;
    if (httpStatus !== null && NO_ACCESS_STATUSES.includes(httpStatus)) {
      dispatch(setPermissions({ permissions: [] }));
      return;
    }
    if (queryError && !hasData) {
      Sentry.captureException(
        new PermissionsLoadError(
          `Failed to load user permissions (status ${
            "status" in queryError ? queryError.status : undefined
          })`,
          queryError
        )
      );
      dispatch(setPermissionsError());
      return;
    }
    if (hasData) {
      // Only management roles: the roles list is management roles, and customer roles are another table
      const permissions = denormalizePermissions(
        user?.userManagementRoles ?? [],
        roles ?? []
      );
      dispatch(setPermissions({ permissions }));
    }
  }, [email, queryError, hasData, user, roles, dispatch]);

  return null;
};

export default PermissionService;
